import { useEffect, useState } from 'react';
import { LoginSessionChoices, type LoginSessionChoice } from '../components/login-session-choice';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ArrowLeft,
  Boxes,
  Building2,
  CheckCircle2,
  Eye,
  EyeOff,
  Gauge,
  KeyRound,
  LockKeyhole,
  ShieldCheck,
  UserRound,
} from 'lucide-react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useAuth } from '@ochal/auth';
import { Alert, Button, Checkbox, Input } from '@ochal/ui';
import { fa } from '../i18n/fa';
import { loginBaselineFa } from '../i18n/login-baseline';
import { ApiClient, ApiError } from '@ochal/api-client';
import { parseEnvironment } from '@ochal/config';
import { loginCaptchaFa } from '../i18n/login-captcha';

type CaptchaChallenge = { id: string; image: string; expiresAt: string };
const environment = parseEnvironment(import.meta.env);
const captchaClient = new ApiClient({ baseUrl: environment.VITE_API_BASE_URL, timeout: environment.VITE_API_TIMEOUT_MS });

const schema = z.object({
  identity: z.string().min(3, fa.auth.identityRequired),
  password: z.string().min(6, fa.auth.passwordMin),
  remember: z.boolean(),
});

type Form = z.infer<typeof schema>;

export function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState('');
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captcha, setCaptcha] = useState<CaptchaChallenge | null>(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [sessionChoices, setSessionChoices] = useState<LoginSessionChoice[]>([]);
  const [replaceSessionId, setReplaceSessionId] = useState('');

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    register,
    handleSubmit,
    getValues,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: {
      identity: '',
      password: '',
      remember: false,
    },
  });

  const identity = watch('identity');
  useEffect(() => { setSessionChoices([]); setReplaceSessionId(''); }, [identity]);
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  async function submit(data: Form) {
    try {
      setServerError('');
      if (sessionChoices.length && !replaceSessionId) { setServerError('یک نشست را برای جایگزینی انتخاب کنید.'); return; }
      if (captchaRequired && (!captcha || !captchaAnswer.trim())) {
        setServerError(loginCaptchaFa.required);
        return;
      }
      await login({ ...data, ...(replaceSessionId ? { replaceSessionId } : {}), ...(captchaRequired && captcha ? { captchaId: captcha.id, captchaAnswer } : {}) });

      const from =
        (location.state as { from?: { pathname: string } } | null)?.from?.pathname ??
        '/';

      navigate(from, { replace: true });
    } catch (error) {
      const envelope = error instanceof ApiError ? error.details as { error?: { code?: string; details?: { captchaRequired?: boolean; sessions?: LoginSessionChoice[] } } } | undefined : undefined;
      const code = envelope?.error?.code;
      const messages = loginCaptchaFa.errors;
      setReplaceSessionId('');
      if (code === 'SESSION_LIMIT_REACHED' && Array.isArray(envelope?.error?.details?.sessions)) {
        setSessionChoices(envelope.error.details.sessions);
        if (captchaRequired) await refreshCaptcha(data.identity);
      } else { setSessionChoices([]); }
      const noCaptchaSubmitted = !captchaRequired || !captcha || !captchaAnswer.trim();
      setServerError(code === 'LOGIN_CAPTCHA_REQUIRED' && noCaptchaSubmitted
        ? loginCaptchaFa.activationRequired
        : (code && messages[code]) || (error instanceof Error ? error.message : fa.auth.invalid));
      if (envelope?.error?.details?.captchaRequired) {
        setCaptchaRequired(true);
        await refreshCaptcha(data.identity);
      }
    }
  }

  async function refreshCaptcha(identity = getValues('identity')) {
    setCaptchaLoading(true);
    setCaptcha(null);
    setCaptchaAnswer('');
    try {
      const response = await captchaClient.post<{ data: CaptchaChallenge }>('/auth/login-captcha', { identity });
      setCaptcha(response.data);
    } catch {
      setServerError(loginCaptchaFa.loadFailed);
    } finally { setCaptchaLoading(false); }
  }

  return (
    <main className="ochal-login">
      <section className="ochal-login__form-side">
        <div className="ochal-login__form-shell">
          <form
            className="ochal-login-card"
            onSubmit={handleSubmit(submit)}
            noValidate
          >
            <header className="ochal-login-card__header">
              <span className="ochal-login-card__lock">
                <LockKeyhole />
              </span>

              <h1>{loginBaselineFa.title}</h1>

              <p>
                {loginBaselineFa.description}
              </p>
            </header>

            {new URLSearchParams(location.search).get('reason') === 'session-expired' && (
              <div className="ochal-login-card__error" role="status">
                <Alert variant="info">{sessionMessages.expired}</Alert>
              </div>
            )}

            {serverError && (
              <div className="ochal-login-card__error">
                <Alert variant="danger">
                  <span aria-live="polite">{serverError}</span>
                </Alert>
              </div>
            )}

            <label className="ochal-login-field">
              <span>{loginBaselineFa.identity}</span>

              <div className="ochal-login-control">
                <UserRound aria-hidden="true" />

                <Input
                  id="login-identity"
                  aria-label={fa.auth.identity}
                  autoComplete="username"
                  placeholder={fa.auth.identityPlaceholder}
                  {...register('identity')}
                  aria-invalid={Boolean(errors.identity)}
                  aria-describedby="login-identity-error"
                />
              </div>

              <small id="login-identity-error">
                {errors.identity?.message}
              </small>
            </label>

            <label className="ochal-login-field">
              <span>{loginBaselineFa.password}</span>

              <div className="ochal-login-control ochal-login-control--password">
                <KeyRound aria-hidden="true" />

                <Input
                  id="login-password"
                  aria-label={fa.auth.password}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder={fa.auth.passwordPlaceholder}
                  {...register('password')}
                  aria-invalid={Boolean(errors.password)}
                  aria-describedby="login-password-error"
                />

                <button
                  type="button"
                  className="ochal-login-control__eye"
                  aria-label={
                    showPassword
                      ? fa.auth.hidePassword
                      : fa.auth.showPassword
                  }
                  onClick={() => setShowPassword((value) => !value)}
                >
                  {showPassword ? <EyeOff /> : <Eye />}
                </button>
              </div>

              <small id="login-password-error">
                {errors.password?.message}
              </small>
            </label>

            <div className="ochal-login-card__options">
              <label className="ochal-login-remember">
                <Checkbox id="login-remember" {...register('remember')} />
                <span>{loginBaselineFa.remember}</span>
              </label>

              <span className="ochal-login-secure">
                <ShieldCheck />
                {loginBaselineFa.secure}
              </span>
            </div>

            {captchaRequired && <section className="ochal-login-captcha" aria-label={loginCaptchaFa.title}>
              <p>{loginCaptchaFa.prompt}</p>
              <div className="ochal-login-captcha__image">
                {captchaLoading ? <span role="status">{loginCaptchaFa.loading}</span> : captcha ? <img src={captcha.image} width="200" height="64" alt={loginCaptchaFa.imageAlt} /> : <span>{loginCaptchaFa.unavailable}</span>}
                <Button type="button" disabled={isSubmitting || captchaLoading} onClick={() => void refreshCaptcha()}>{loginCaptchaFa.refresh}</Button>
              </div>
              <label htmlFor="login-captcha-answer">{loginCaptchaFa.label}</label>
              <Input id="login-captcha-answer" name="captchaAnswer" inputMode="numeric" dir="ltr" autoComplete="off" maxLength={12} value={captchaAnswer} onChange={event => setCaptchaAnswer(event.target.value)} />
              <small>{loginCaptchaFa.help}</small>
            </section>}

            {sessionChoices.length > 0 && <LoginSessionChoices sessions={sessionChoices} selected={replaceSessionId} onSelect={setReplaceSessionId}/>}
            <Button
              className="ochal-login-submit"
              type="submit"
              disabled={isSubmitting || captchaLoading}
            >
              <span>
                {isSubmitting ? fa.auth.submitting : sessionChoices.length ? 'قطع نشست انتخاب‌شده و ورود' : loginBaselineFa.submit}
              </span>

              {!isSubmitting && <ArrowLeft />}
            </Button>

            <p className="ochal-login-card__help">
              {loginBaselineFa.help}
            </p>
          </form>

          <footer className="ochal-login__form-footer">
            <span>{loginBaselineFa.platform}</span>
            <span>{loginBaselineFa.copyright}</span>
          </footer>
        </div>
      </section>

      <section className="ochal-login__brand-side">
        <div className="ochal-login__hero-image" />
        <div className="ochal-login__hero-overlay" />

        <div className="ochal-login__enterprise-badge">
          <Building2 />
          <span>{loginBaselineFa.edition}</span>
        </div>

        <div className="ochal-login__brand-content">
          <div className="ochal-login__hero-copy">
            <img
              className="ochal-login__hero-logo"
              src="/brand/ochal-logo-577b53b5575a68ce.png"
              alt={loginBaselineFa.brand}
            />

            <h2>{loginBaselineFa.company}</h2>

            <strong>{loginBaselineFa.slogan}</strong>

            <p>
              {loginBaselineFa.overview}
            </p>
          </div>

          <div className="ochal-login__features">
            <article>
              <span><Gauge /></span>
              <h3>{loginBaselineFa.fast}</h3>
              <p>{loginBaselineFa.fastDescription}</p>
            </article>

            <article>
              <span><Boxes /></span>
              <h3>{loginBaselineFa.modular}</h3>
              <p>{loginBaselineFa.modularDescription}</p>
            </article>

            <article>
              <span><ShieldCheck /></span>
              <h3>{loginBaselineFa.safe}</h3>
              <p>{loginBaselineFa.safeDescription}</p>
            </article>
          </div>

          <footer className="ochal-login__brand-footer">
            <span>
              <CheckCircle2 />
              {loginBaselineFa.ready}
            </span>

            <small>{loginBaselineFa.infrastructure}</small>
          </footer>
        </div>
      </section>
    </main>
  );
}
import { sessionMessages } from '../i18n/session';
